import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Save, Scale } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';

const CYCLES = [
    { id: 'monthly', label: 'Every month' },
    { id: 'quarterly', label: 'Every quarter' }
];

const CycleChooser = ({ value, onChange }) => (
    <div className="space-y-2">
        <p className="flex items-center gap-2 text-sm font-medium"><CalendarClock className="h-4 w-4 text-sky-600" /> Refresher frequency</p>
        <div className="inline-flex rounded-lg border p-1" role="radiogroup" data-testid="eeoc-cycle-chooser">
            {CYCLES.map((c) => (
                <button key={c.id} type="button" role="radio" aria-checked={value === c.id} onClick={() => onChange(c.id)}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${value === c.id ? 'bg-sky-600 text-white' : 'text-muted-foreground hover:bg-muted'}`}
                    data-testid={`eeoc-cycle-${c.id}`}>
                    {c.label}
                </button>
            ))}
        </div>
        <p className="text-xs text-muted-foreground">
            Everyone with EEOC access must pass all 8 modules again each {value === 'monthly' ? 'calendar month' : 'calendar quarter'}. The admin report shows who completed each period.
        </p>
    </div>
);

const EEOCScoringSettings = () => {
    const { api } = useAuth();
    const [settings, setSettings] = useState(null);
    const [value, setValue] = useState(80);
    const [cycle, setCycle] = useState('quarterly');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/eeoc-training/admin/settings')
            .then(({ data }) => { setSettings(data); setValue(data.min_passing_score); setCycle(data.recert_cycle); })
            .catch(() => toast.error('Could not load EEOC scoring settings'));
    }, [api]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put('/eeoc-training/admin/settings', { min_passing_score: value, recert_cycle: cycle });
            setSettings(data);
            toast.success(`EEOC settings saved: pass mark ${data.min_passing_score}, refresher ${data.recert_cycle}`);
        } catch (error) {
            toast.error(error.response?.data?.detail?.[0]?.msg || 'Failed to save EEOC settings');
        } finally {
            setSaving(false);
        }
    };

    const unchanged = settings && value === settings.min_passing_score && cycle === settings.recert_cycle;

    return (
        <Card data-testid="eeoc-scoring-settings">
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-sky-600" /> EEOC Compliance Scoring</CardTitle>
                <CardDescription>Set the minimum passing score and how often everyone must redo EEOC training.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {!settings ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                    <>
                        <div className="flex items-end justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium">Minimum passing score</p>
                                <p className="text-xs text-muted-foreground">Range 50-100 · default {settings.default}</p>
                            </div>
                            <span className="text-4xl font-bold tabular-nums text-sky-600" data-testid="eeoc-passing-score-value">{value}</span>
                        </div>
                        <Slider min={50} max={100} step={1} value={[value]} onValueChange={([v]) => setValue(v)} data-testid="eeoc-passing-score-slider" />
                        <p className="rounded-lg bg-sky-500/5 px-3 py-2 text-sm text-muted-foreground">
                            Recruiters <span className="font-medium text-emerald-600">PASS</span> a session when their overall compliance score is {value} or higher, otherwise they <span className="font-medium text-red-600">FAIL</span> and can retake the scenarios. Completed sessions keep the pass mark that applied when they finished.
                        </p>
                        <CycleChooser value={cycle} onChange={setCycle} />
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-muted-foreground">
                                {settings.updated_at ? `Last updated ${new Date(settings.updated_at).toLocaleString()}${settings.updated_by ? ` by ${settings.updated_by}` : ''}` : 'Using default values'}
                            </p>
                            <Button onClick={save} disabled={saving || unchanged} data-testid="eeoc-save-settings-btn">
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default EEOCScoringSettings;
